<template>
  <div class="col-12">
    <div class="card mb-5" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">{{ t('class_fees.add_title') }}</h6>
          </div>
        </div>
      </div>
      
      <div class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-md-8 ms-3">
            <!-- Duplicate Fee Warning -->
            <div v-if="duplicateFee" class="alert alert-warning alert-dismissible fade show" role="alert">
              <div class="d-flex align-items-center">
                <i class="fas fa-exclamation-triangle me-2"></i>
                <div>
                  <strong>{{ t('class_fees.duplicate_fee_warning') }}</strong>
                  <p class="mb-0">
                    {{ t('class_fees.duplicate_fee_message', {
                      class: duplicateFee.class_name,
                      year: duplicateFee.academic_year_name
                    }) }}
                  </p>
                  <small class="text-muted">
                    {{ t('class_fees.current_amount') }}: {{ formatCurrency(duplicateFee.amount) }}
                  </small>
                </div>
              </div>
              <button type="button" class="btn-close" @click="duplicateFee = null"></button>
            </div>

            <form @submit.prevent="submitForm" @keydown.enter.prevent>
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('class_fees.class') }}</label>
                  <select 
                    v-model="form.class_id" 
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.class_id }"
                    required
                    @blur="validateField('class_id')"
                    @change="checkForDuplicate"
                    :disabled="loading"
                  >
                    <option value="" disabled>{{ t('class_fees.select_class') }}</option>
                    <option v-for="classItem in classes" :value="classItem.id" :key="classItem.id">
                      {{ classItem.name }}
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.class_id }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('class_fees.academic_year') }}</label>
                  <select 
                    v-model="form.academic_year_id" 
                    class="form-select borderless-input"
                    :class="{ 'is-invalid': errors.academic_year_id }"
                    required
                    @blur="validateField('academic_year_id')"
                    @change="checkForDuplicate"
                    :disabled="loading"
                  >
                    <option value="" disabled>{{ t('class_fees.select_academic_year') }}</option>
                    <option v-for="year in academicYears" :value="year.id" :key="year.id">
                      {{ year.name }} ({{ year.year }})
                    </option>
                  </select>
                  <div class="invalid-feedback">{{ errors.academic_year_id }}</div>
                </div>
                
                <div class="col-md-6">
                  <label class="bmd-label-float">{{ t('class_fees.amount') }}</label>
                  <div class="input-group">
                    <span class="input-group-text">{{ t('class_fees.lyd_abbr') }}</span>
                    <input 
                      type="number" 
                      v-model="form.amount" 
                      class="form-control borderless-input"
                      :class="{ 'is-invalid': errors.amount }"
                      required 
                      min="0" 
                      step="0.01"
                      @blur="validateField('amount')"
                      :disabled="loading"
                    >
                  </div>
                  <div class="invalid-feedback">{{ errors.amount }}</div>
                </div>
           
                <div class="col-12 mt-4">
                  <div class="d-flex gap-3">
                    <button 
                      type="submit" 
                      class="btn btn-primary flex-grow-1"
                      :disabled="loading || duplicateFee"
                    >
                      <span v-if="loading">
                        <i class="fas fa-spinner fa-spin me-2"></i>
                        {{ t('class_fees.saving') }}
                      </span>
                      <span v-else>
                        <i class="fas fa-save ms-2"></i>
                        {{ t('class_fees.add_button') }}
                      </span>
                    </button>
                    <router-link 
                      :to="{ name: 'class-fees.index' }" 
                      class="btn btn-outline-secondary flex-grow-1"
                      :disabled="loading"
                    >
                      <i class="fas fa-times ms-2"></i>
                      {{ t('class_fees.cancel') }}
                    </router-link>
                  </div>
                </div>

                <!-- Action buttons when duplicate exists -->
                <div v-if="duplicateFee" class="col-12">
                  <div class="card border-warning">
                    <div class="card-body">
                      <h6 class="card-title text-warning">
                        <i class="fas fa-lightbulb me-2"></i>
                        {{ t('class_fees.duplicate_actions_title') }}
                      </h6>
                      <div class="d-flex gap-2 flex-wrap">
                        <router-link 
                          :to="{ name: 'class-fees.edit', params: { id: duplicateFee.id } }" 
                          class="btn btn-warning btn-sm"
                        >
                          <i class="fas fa-edit me-1"></i>
                          {{ t('class_fees.edit_existing_fee') }}
                        </router-link>
                        <button 
                          class="btn btn-outline-secondary btn-sm" 
                          @click="resetForm"
                        >
                          <i class="fas fa-redo me-1"></i>
                          {{ t('class_fees.change_selection') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import api from '@/api';

const { t, locale } = useI18n();
const toast = useToast();
const router = useRouter();
const loading = ref(false);
const checkingDuplicate = ref(false);
const classes = ref([]);
const academicYears = ref([]);
const duplicateFee = ref(null);

const form = ref({
  class_id: '',
  academic_year_id: '',
  amount: 0,
  description: ''
});

const errors = ref({});

onMounted(async () => {
  await Promise.all([fetchAcademicYears(), fetchClasses()]);
  
  // Set default academic year to current active one
  const activeYear = academicYears.value.find(year => year.is_active);
  if (activeYear) {
    form.value.academic_year_id = activeYear.id;
  }
});

const fetchAcademicYears = async () => {
  try {
    const response = await api.get('/academic-years');
    academicYears.value = response.data.data;
  } catch (error) {
    toast.error(extractApiMessage(error, 'class_fees.load_years_failed'));
  }
};

const fetchClasses = async () => {
  try {
    const response = await api.get('/classes', { params: { all: true } });
    classes.value = response.data.data;
  } catch (error) {
    toast.error(extractApiMessage(error, 'class_fees.load_classes_failed'));
  }
};

// Check for duplicate fee when class or academic year changes
const checkForDuplicate = async () => {
  if (!form.value.class_id || !form.value.academic_year_id) {
    duplicateFee.value = null;
    return;
  }

  checkingDuplicate.value = true;
  try {
    const response = await api.get('/payments/class-fees/check', {
      params: {
        class_id: form.value.class_id,
        academic_year_id: form.value.academic_year_id
      }
    });
    
    if (response.data.exists) {
      duplicateFee.value = response.data.existing_fee;
    } else {
      duplicateFee.value = null;
    }
  } catch (error) {
    console.error('Error checking duplicate:', error);
    duplicateFee.value = null;
  } finally {
    checkingDuplicate.value = false;
  }
};

// Watch for changes in class or academic year to check for duplicates
watch(
  () => [form.value.class_id, form.value.academic_year_id],
  () => {
    if (form.value.class_id && form.value.academic_year_id) {
      // Debounce the duplicate check
      setTimeout(checkForDuplicate, 500);
    } else {
      duplicateFee.value = null;
    }
  }
);

const validateField = (field) => {
  if (!form.value[field] && field !== 'description') {
    errors.value[field] = t('validation.required');
    return false;
  }
  
  if (field === 'amount' && form.value.amount < 0) {
    errors.value.amount = t('validation.amount_positive');
    return false;
  }
  
  delete errors.value[field];
  return true;
};

const validateForm = () => {
  let isValid = true;
  const requiredFields = ['class_id', 'academic_year_id', 'amount'];
  
  requiredFields.forEach(field => {
    if (!validateField(field)) {
      isValid = false;
    }
  });
  
  return isValid;
};

const submitForm = async () => {
  if (!validateForm()) {
    toast.warning(t('validation.please_correct_errors'));
    return;
  }

  if (duplicateFee.value) {
    toast.warning(t('class_fees.duplicate_fee_warning_short'));
    return;
  }

  loading.value = true;
  try {
    const submitData = {
      class_id: form.value.class_id,
      academic_year_id: form.value.academic_year_id,
      amount: parseFloat(form.value.amount),
      description: form.value.description
    };

    const response = await api.post('/payments/class-fees', submitData);
    
    const message = response.data.message;
    const successMessage = typeof message === 'object' 
      ? message[locale.value] || message.en 
      : message;
    
    toast.success(successMessage);
    router.push({ name: 'class-fees.index' });
  } catch (error) {
    console.error('Error creating class fee:', error);
    
    if (error.response?.status === 422) {
      const errorData = error.response.data;
      
      // Handle duplicate fee error with better message
      if (errorData.message) {
        const errorMessage = typeof errorData.message === 'object'
          ? errorData.message[locale.value] || errorData.message.en
          : errorData.message;
        
        toast.error(errorMessage);
        
        // If the API returns the existing fee, show it
        if (errorData.existing_fee) {
          duplicateFee.value = errorData.existing_fee;
        }
      } else if (errorData.errors) {
        errors.value = errorData.errors;
        Object.values(errorData.errors).flat().forEach(message => {
          toast.error(message);
        });
      }
    } else {
      toast.error(extractApiMessage(error, 'class_fees.save_failed'));
    }
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  duplicateFee.value = null;
  form.value.class_id = '';
  form.value.academic_year_id = academicYears.value.find(year => year.is_active)?.id || '';
};

const extractApiMessage = (error, fallbackKey = 'errors.generic') => {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
};

const formatCurrency = (amount) => {
  const numericAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  if (isNaN(numericAmount)) return "0 " + t('class_fees.lyd_abbr');

  const formatted = numericAmount % 1 === 0
    ? numericAmount.toString()
    : numericAmount.toFixed(2);

  return formatted + " " + t('class_fees.lyd_abbr');
};
</script>